"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
} from "@mui/material"
import { AttachMoney, ShoppingBag, Pending, CheckCircle } from "@mui/icons-material"

export default function EarningsPage() {
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [orders, setOrders] = useState([])
  const [filteredOrders, setFilteredOrders] = useState([])

  // Initialize mock data on mount
  useEffect(() => {
    const storedOrders = localStorage.getItem("vendorOrders")
    if (storedOrders) {
      setOrders(JSON.parse(storedOrders))
      setFilteredOrders(JSON.parse(storedOrders))
    } else {
      // Mock initial data
      const mockOrders = [
        {
          id: "ORD-001",
          date: "2025-01-15",
          customer: "Sarah Johnson",
          items: "Summer Dress, Sandals",
          amount: 129.99,
          status: "paid",
        },
        {
          id: "ORD-002",
          date: "2025-01-18",
          customer: "Mike Chen",
          items: "Leather Jacket",
          amount: 299.99,
          status: "unpaid",
        },
        {
          id: "ORD-003",
          date: "2025-01-20",
          customer: "Emily Davis",
          items: "Denim Jeans, T-Shirt",
          amount: 89.99,
          status: "paid",
        },
        {
          id: "ORD-004",
          date: "2025-01-22",
          customer: "James Wilson",
          items: "Sneakers",
          amount: 149.99,
          status: "unpaid",
        },
        {
          id: "ORD-005",
          date: "2025-01-25",
          customer: "Lisa Anderson",
          items: "Evening Gown",
          amount: 399.99,
          status: "paid",
        },
      ]
      localStorage.setItem("vendorOrders", JSON.stringify(mockOrders))
      setOrders(mockOrders)
      setFilteredOrders(mockOrders)
    }
  }, [])

  const handleFilter = () => {
    if (!startDate && !endDate) {
      setFilteredOrders(orders)
      return
    }

    const filtered = orders.filter((order) => {
      const orderDate = new Date(order.date)
      const start = startDate ? new Date(startDate) : null
      const end = endDate ? new Date(endDate) : null

      if (start && end) {
        return orderDate >= start && orderDate <= end
      } else if (start) {
        return orderDate >= start
      } else if (end) {
        return orderDate <= end
      }
      return true
    })

    setFilteredOrders(filtered)
  }

  const handleClearFilter = () => {
    setStartDate("")
    setEndDate("")
    setFilteredOrders(orders)
  }

  const calculateStats = () => {
    const totalOrders = filteredOrders.length
    const paidOrders = filteredOrders.filter((o) => o.status === "paid").length
    const unpaidOrders = filteredOrders.filter((o) => o.status === "unpaid").length
    const totalEarnings = filteredOrders.filter((o) => o.status === "paid").reduce((sum, o) => sum + o.amount, 0)
    const unpaidAmount = filteredOrders.filter((o) => o.status === "unpaid").reduce((sum, o) => sum + o.amount, 0)

    return { totalOrders, paidOrders, unpaidOrders, totalEarnings, unpaidAmount }
  }

  const stats = calculateStats()

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, color: "#1a1a1a" }}>
          Earnings Dashboard
        </Typography>

        {/* Date Filter */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Filter by Date Range
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="contained" onClick={handleFilter} fullWidth>
                  Apply
                </Button>
                <Button variant="outlined" onClick={handleClearFilter} fullWidth>
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <ShoppingBag sx={{ color: "#1976d2", mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Total Orders
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats.totalOrders}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <CheckCircle sx={{ color: "#2e7d32", mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Paid Orders
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                  {stats.paidOrders}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <AttachMoney sx={{ color: "#2e7d32", mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Total Earnings
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                  ${stats.totalEarnings.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Pending sx={{ color: "#ed6c02", mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Unpaid Amount
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#ed6c02" }}>
                  ${stats.unpaidAmount.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Orders Table */}
        <Paper sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Order History
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Order ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{order.id}</TableCell>
                      <TableCell>{order.date}</TableCell>
                      <TableCell>{order.customer}</TableCell>
                      <TableCell>{order.items}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>${order.amount.toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          label={order.status}
                          color={order.status === "paid" ? "success" : "warning"}
                          size="small"
                          sx={{ textTransform: "capitalize", fontWeight: 600 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No orders found for the selected date range</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </Box>
  )
}
